// Pre-rendering (§13.1, §25): every one-shot, voice syllable, crowd loop, instrument note and drum
// is rendered to AudioBuffers with OfflineAudioContext after unlock, at most `perPump` jobs started
// per frame so the main thread never blocks. Jobs run in tier order (essential SFX first, so they
// are ready within 300 ms), then FIFO; `promote` moves a song's instruments to the front when its
// round starts. A sound that is not rendered yet simply does not play.
import { createKit, type Kit } from './sfx/kit';
import type { LoopSpec } from './sounds';

export interface PostSpec {
  /** Level of the loudest 50 ms window (one-shots) or the RMS (loops), dB relative to the reference. */
  db: number;
  loop?: LoopSpec;
}

export interface RenderJob {
  key: string;
  /** Rendered length, seconds. */
  dur: number;
  variants: number;
  seed: number;
  tier: number;
  render(k: Kit): void;
  post: PostSpec;
}

/** Loudness reference: the loudest 50 ms of a one-shot (or a loop's RMS) sits at −12 dBFS + db. */
export const REFERENCE_DBFS = -12;
/** Hard ceiling for any rendered sample. */
export const PEAK_CEILING = 10 ** (-1 / 20);

/**
 * Post-processes one rendered variant in place (or into a new array for loops): NaN guard, loop
 * folding with an equal-power crossfade, zero-ended edges, loudness normalization and the peak
 * ceiling. Pure; returns the final samples.
 */
/**
 * Playback gain a rendered buffer still needs to reach its loudness target: peaky sounds hit the
 * −1 dBFS ceiling first, and the rest of their trim is applied at playback (never clipping a
 * buffer, never losing a trim). The player, the sampler and the lab multiply by it.
 */
export const BUFFER_GAIN = new WeakMap<AudioBuffer, number>();

export function bufferGain(b: AudioBuffer): number {
  return BUFFER_GAIN.get(b) ?? 1;
}

export function postProcess(data: Float32Array<ArrayBuffer>, sampleRate: number, spec: PostSpec, info?: { residual: number }): Float32Array<ArrayBuffer> {
  for (let i = 0; i < data.length; i++) if (!Number.isFinite(data[i])) data[i] = 0;
  let out = data;
  if (spec.loop) {
    const s = Math.round(spec.loop.start * sampleRate);
    const len = Math.round(spec.loop.length * sampleRate);
    const fade = Math.min(len, Math.round(spec.loop.fade * sampleRate));
    out = new Float32Array(len);
    for (let i = 0; i < len; i++) {
      const head = data[s + i] ?? 0;
      if (i < fade) {
        const w = (i + 0.5) / fade;
        const tail = data[s + len + i] ?? 0;
        out[i] = head * Math.sin((w * Math.PI) / 2) + tail * Math.cos((w * Math.PI) / 2);
      } else {
        out[i] = head;
      }
    }
  } else {
    // One-shots start and end at exactly zero: a 0.5 ms raised-cosine fade-in keeps attacks crisp
    // without a step, and a 3 ms fade-out.
    const fin = Math.min(out.length, Math.max(1, Math.round(sampleRate * 0.0005)));
    for (let i = 0; i < fin; i++) out[i] *= 0.5 - 0.5 * Math.cos((Math.PI * i) / fin);
    const fout = Math.min(out.length, Math.round(sampleRate * 0.003));
    for (let i = 0; i < fout; i++) out[out.length - 1 - i] *= i / fout;
  }
  let level: number;
  if (spec.loop) {
    let e = 0;
    for (let i = 0; i < out.length; i++) e += out[i] * out[i];
    level = Math.sqrt(e / Math.max(1, out.length));
  } else {
    const win = Math.max(1, Math.round(sampleRate * 0.05));
    let acc = 0;
    let best = 0;
    for (let i = 0; i < out.length; i++) {
      acc += out[i] * out[i];
      if (i >= win) acc -= out[i - win] * out[i - win];
      if (acc > best) best = acc;
    }
    level = Math.sqrt(Math.max(0, best) / Math.min(win, out.length));
  }
  if (level <= 1e-9) return out;
  const desired = 10 ** ((REFERENCE_DBFS + spec.db) / 20) / level;
  let gain = desired;
  let peak = 0;
  for (let i = 0; i < out.length; i++) peak = Math.max(peak, Math.abs(out[i]));
  if (peak * gain > PEAK_CEILING) gain = PEAK_CEILING / peak;
  for (let i = 0; i < out.length; i++) out[i] *= gain;
  if (info) info.residual = desired / gain;
  return out;
}

export interface PreRenderStats {
  started: number; done: number; failed: number; queued: number; inFlight: number;
  /** ms from the first job until every essential (tier 0) sound is playable. */
  essentialMs: number;
  allMs: number;
  /** Main-thread ms spent building render graphs and post-processing. */
  mainMs: number;
  /** The most expensive main-thread steps (diagnostics). */
  slowest: { key: string; phase: 'build' | 'post'; ms: number }[];
}

/** A sound store: key → rendered variants. */
export type SoundStore = Map<string, AudioBuffer[]>;

/** One variant of one job. */
interface Task { job: RenderJob; variant: number; tier: number }
interface Finished { task: Task; data: AudioBuffer | null }

/**
 * The render queue. The unit of work is one variant: essential sounds render their first variant
 * at tier 0 (playable at once) and their other variants later. Each `pump` spends at most
 * `budgetMs` of main-thread time: first on post-processing finished renders, then on starting up
 * to `maxStarts` new ones. Rendering itself runs off the main thread.
 */
export class PreRenderer {
  private readonly queue: Task[] = [];
  private readonly finished: Finished[] = [];
  private readonly known = new Set<string>();
  private inFlight = 0;
  private started = 0;
  private done = 0;
  private failed = 0;
  private startAt = -1;
  private essentialMs = -1;
  private essentialLeft = 0;
  private allMs = -1;
  private mainMs = 0;
  private readonly slowest: { key: string; phase: 'build' | 'post'; ms: number }[] = [];
  private warned = false;
  private readonly waiters: (() => void)[] = [];

  constructor(
    private readonly sampleRate: number,
    readonly store: SoundStore,
    /** Most renders allowed in flight (they run off the main thread). */
    private readonly maxInFlight = 24,
    /** When the clock for `essentialMs` starts (the unlock request); default: the first job. */
    startAt = -1,
  ) {
    this.startAt = startAt;
  }

  /** Queues a job unless it is already queued or rendered. */
  add(job: RenderJob): void {
    if (this.known.has(job.key)) return;
    this.known.add(job.key);
    for (let v = 0; v < job.variants; v++) {
      const tier = v > 0 && job.tier === 0 ? 2 : job.tier;
      this.insert({ job, variant: v, tier });
      if (tier === 0) this.essentialLeft++;
    }
    if (this.startAt < 0) this.startAt = performance.now();
  }

  private insert(t: Task): void {
    let i = this.queue.length;
    while (i > 0 && this.queue[i - 1].tier > t.tier) i--;
    this.queue.splice(i, 0, t);
  }

  /** Moves queued jobs with these keys right behind the essential ones (tier 1), in order. */
  promote(keys: ReadonlySet<string>): void {
    const moved = this.queue.filter((t) => keys.has(t.job.key) && t.tier > 1).map((t) => ({ ...t, tier: 1 }));
    if (moved.length === 0) return;
    const rest = this.queue.filter((t) => !(keys.has(t.job.key) && t.tier > 1));
    const cut = rest.findIndex((t) => t.tier > 1);
    const at = cut < 0 ? rest.length : cut;
    this.queue.length = 0;
    this.queue.push(...rest.slice(0, at), ...moved, ...rest.slice(at));
  }

  has(key: string): boolean {
    return this.store.has(key);
  }

  /**
   * One frame's share: post-process finished renders, then start up to `maxStarts` new ones. The
   * first start always runs; later ones only while under half the budget (graph building of a big
   * recipe can take a few ms on its own).
   */
  pump(maxStarts = 4, budgetMs = 2.5): void {
    const t0 = performance.now();
    while (this.finished.length > 0 && performance.now() - t0 < budgetMs) this.complete(this.finished.shift() as Finished);
    let n = 0;
    while (n < maxStarts && this.inFlight < this.maxInFlight && this.queue.length > 0 && (n === 0 ? performance.now() - t0 < budgetMs : performance.now() - t0 < budgetMs / 2)) {
      this.start(this.queue.shift() as Task);
      n++;
    }
    this.mainMs += performance.now() - t0;
  }

  /**
   * Starts queued essential (tier 0) tasks: all of them, or (with `budgetMs`) as many as fit in
   * that much main-thread time, at least one. Returns true while essentials remain queued: the
   * engine calls it once per task, so building ~40 render graphs never blocks a frame.
   */
  startEssentials(budgetMs = Infinity): boolean {
    const t0 = performance.now();
    let n = 0;
    while (this.queue.length > 0 && this.queue[0].tier === 0 && (n === 0 || performance.now() - t0 < budgetMs)) {
      this.start(this.queue.shift() as Task);
      n++;
    }
    this.mainMs += performance.now() - t0;
    return this.queue.length > 0 && this.queue[0].tier === 0;
  }

  get idle(): boolean {
    return this.queue.length === 0 && this.inFlight === 0 && this.finished.length === 0;
  }

  /** Resolves once nothing is queued, rendering or waiting for post-processing. */
  whenIdle(): Promise<void> {
    if (this.idle) return Promise.resolve();
    return new Promise((resolve) => this.waiters.push(resolve));
  }

  get stats(): PreRenderStats {
    return {
      started: this.started, done: this.done, failed: this.failed, queued: this.queue.length, inFlight: this.inFlight,
      essentialMs: this.essentialMs, allMs: this.allMs, mainMs: this.mainMs, slowest: [...this.slowest],
    };
  }

  private note(key: string, phase: 'build' | 'post', ms: number): void {
    if (this.slowest.length >= 6 && ms <= this.slowest[this.slowest.length - 1].ms) return;
    this.slowest.push({ key, phase, ms: Math.round(ms * 100) / 100 });
    this.slowest.sort((a, b) => b.ms - a.ms);
    this.slowest.length = Math.min(this.slowest.length, 6);
  }

  private start(task: Task): void {
    this.inFlight++;
    this.started++;
    const { job, variant } = task;
    const t0 = performance.now();
    let rendering: Promise<AudioBuffer>;
    try {
      const length = Math.max(1, Math.ceil(job.dur * this.sampleRate));
      const ctx = new OfflineAudioContext(1, length, this.sampleRate);
      const dc = ctx.createBiquadFilter();
      dc.type = 'highpass';
      dc.frequency.value = 15;
      dc.connect(ctx.destination);
      job.render(createKit(ctx, variant, dc, job.dur, job.seed));
      rendering = ctx.startRendering();
    } catch (err) {
      rendering = Promise.reject(err);
    }
    this.note(job.key, 'build', performance.now() - t0);
    rendering.then(
      // Essentials are post-processed at once (playable without waiting for a frame); the rest
      // waits for a pump's budget.
      (data) => {
        if (task.tier === 0) this.completeNow({ task, data });
        else this.finished.push({ task, data });
      },
      (err: unknown) => {
        if (!this.warned) {
          this.warned = true;
          console.warn(`[audio] could not render '${job.key}'`, err);
        }
        this.finished.push({ task, data: null });
      },
    ).finally(() => {
      this.inFlight--;
    });
  }

  private completeNow(f: Finished): void {
    const t0 = performance.now();
    this.complete(f);
    this.mainMs += performance.now() - t0;
  }

  /** Post-processes a finished render into the store (main thread, inside the pump budget). */
  private complete(f: Finished): void {
    const { task, data } = f;
    const t0 = performance.now();
    this.done++;
    if (data) {
      const raw = data.getChannelData(0);
      const info = { residual: 1 };
      const out = postProcess(raw, this.sampleRate, task.job.post, info);
      let buf = data;
      if (out !== raw) {
        buf = new AudioBuffer({ length: out.length, sampleRate: this.sampleRate, numberOfChannels: 1 });
        buf.copyToChannel(out, 0);
      }
      if (info.residual > 1.0001) BUFFER_GAIN.set(buf, info.residual);
      const list = this.store.get(task.job.key);
      if (list) list.push(buf);
      else this.store.set(task.job.key, [buf]);
    } else {
      this.failed++;
    }
    const now = performance.now();
    this.note(task.job.key, 'post', now - t0);
    if (task.tier === 0 && --this.essentialLeft === 0 && this.essentialMs < 0) this.essentialMs = now - this.startAt;
    if (this.idle) {
      if (this.essentialMs < 0) this.essentialMs = now - this.startAt;
      this.allMs = now - this.startAt;
      for (const w of this.waiters.splice(0)) w();
    }
  }
}
